import {Vue, Component} from 'vue-property-decorator';
import VueJS from 'vue';
import { ErrorParseMethodEnum } from '@/services/models/Enums';
import Error from '@/components/errors/error.class';
import { mixins } from 'vue-class-component';
import _ from 'lodash';
import { showPlanLimitsErrorInToast } from '@/services/constants/errors-without-toast.ts';

interface StackNode {
  names: string[];
  value: any;
}

enum States {
  defaultState = 0,
  concatNumeration = 1,
  appendNumeration = 2,
  skipNode = 3,
  appendStringNodeToEnd = 4,
  // states x < 0, substract -x nodes, before append
}

enum NumerationState {
  defaultState = 0,
  hasNumeration = 1
}

enum TranslateState {
  defaultState = 0,
  awaitTranslation = 1
}

export interface TranslateErrorsOptions {
  state?: {[type: string]: number} ;
  translate?: {
    [type: string]: {
      [type: string]: string
    }
  };
  join?: boolean;
  nested?: boolean;
}

@Component
export class FormErrorMixin extends Vue {
  state: any = {};

  get errorsDict() {
    let result: {[type: string]: string[]} = {};

    const keys = Object.keys(this.state);
    keys.forEach((key: string) => {
      if (key.endsWith(':error') && this.state[key]) {
        let newKey = key.replace(':error', '');

        result[newKey] = [ this.state[key] ];
      }
    });

    return result;
  }

  pGetError(field: string) {
    return this.state[field + ':error'] && [this.state[field + ':error']];
  }

  getError(field: string) {
    return this.pGetError(field);
  }

  resetErrors() {
    const keys = Object.keys(this.state);
    keys.forEach((k: string) => {
      if (k.endsWith(':error')) {
        this.state = { ...this.state, [k]: null };
      }
    });
  }

  resetError(key: string) {
    this.state = { ...this.state, [key + ':error']: null };
  }

  get hasErrors() {
    return Object.keys(this.state).find(item=>item.includes(':error'))
  }
}

@Component
export default class FormMixin extends mixins<FormErrorMixin>(FormErrorMixin) {
  clearError(input: string) {
    const key = input + ':error';
    this.$delete(this.state, key)
    return true;
  }

  parseErrors(errors: any, method: ErrorParseMethodEnum = ErrorParseMethodEnum.default) {
    switch(method) {
      case ErrorParseMethodEnum.numerate:
        return this.defaultParseMethod(errors, true);
      default:
        return this.defaultParseMethod(errors);
    }
  }

  get defaultStateConfig(): {[type: string]: number} {
    return {
      'rules': States.concatNumeration,
      'paths': States.concatNumeration,
      'rows': States.concatNumeration,
      'landings': States.appendNumeration,
      'offers': States.appendNumeration,
      'flowInline': States.skipNode,
      'data': States.skipNode,
      'queryConditions': States.skipNode,
      'VALIDATION_FLOW_PATH_AT_LEAST_ONE_OFFER': -2,
      'VALIDATION_FLOW_PATH_AT_LEAST_ONE_LANDING': -2
    };
  }

  prepareError(e: string) {
    return e
      .replace(new RegExp(/[^a-zA-Z_0-9]/, 'g'), '_')
      .replace(new RegExp(/_{2,}/, 'g'), '_');
  }

  getDefaultTranslateErrorsOptions(): TranslateErrorsOptions {
    return {
      state: this.defaultStateConfig,
      translate: {},
      join: true
    }
  }

  translateErrors(errors: any[][], message: string = '', config: TranslateErrorsOptions = {}) {
    // errors can be ['paymentMethodId','',{message: 'Your card's security code is incorrect.', translated: true}]
    // translated:true means don't need to translate this text
    // or ["name", "", "VALIDATION_REQUIRED"]
    // or ["name", <number>, "VALIDATION_REQUIRED"]

    let result: any[] = [];
    let resultNumeration: any[] = [];

    config = {
      ...this.getDefaultTranslateErrorsOptions(),
      ...config
    }

    const stateConfig = config.state;
    const translateConfig = config.translate;
    const join = config.join;

    for(let err of errors) {
      let errorsWithNumeration: string[] = [];
      let translationStack: any = null;

      // cast is needed because TS ignores enum type definition in initialisation
      let state: States = States.defaultState as States;
      let translateState: TranslateState = TranslateState.defaultState as TranslateState;
      let numerationState: NumerationState = NumerationState.defaultState as NumerationState;

      const filteredErrors =  err.filter((e:any) => !!e);

      if(join) {
        filteredErrors
          .forEach((error:any) => {
            if(typeof error === 'number') {
              if(state === States.concatNumeration) {
                errorsWithNumeration[errorsWithNumeration.length - 1] += ` #${error}`;
                state = States.defaultState;
                numerationState = NumerationState.hasNumeration;
              } else if(state === States.appendNumeration) {
                state = States.appendStringNodeToEnd;
                errorsWithNumeration.push(`#${error}`);
                errorsWithNumeration.push('');
                numerationState = NumerationState.hasNumeration;
              }
              return;
            }
            if(typeof error === 'string' || error?.message){
              const errorString = typeof error === 'string' ? error : error?.message;

              if(stateConfig[errorString] === States.skipNode) {
                return;
              } else if(stateConfig[errorString] < 0) {
                for(let i = stateConfig[errorString]; i < 0; i++) {
                  errorsWithNumeration.pop();
                }
              }

              const preparedError = error?.message ? error : this.prepareError(error);

              const field = errorsWithNumeration.length
                ? errorsWithNumeration[errorsWithNumeration.length - 1]
                : '';

              let translation = null;

              if(translateConfig[preparedError] && translateState === TranslateState.defaultState) {
                translationStack = translateConfig[preparedError];
                translateState = TranslateState.awaitTranslation;
              } else if(
                translateState === TranslateState.awaitTranslation
                && translationStack
                && translationStack[preparedError]
              ) {
                translation = (error?.message && error?.translated)
                  ? preparedError
                  : this.t(translationStack[preparedError], [ field ]);
                translateState = TranslateState.defaultState;
              }


              if(!translation) {
                translation =  (error?.message && error?.translated)
                  ? error?.message
                  : this.t(preparedError, [ field ])
              }

              if(state === States.appendStringNodeToEnd) {
                errorsWithNumeration[errorsWithNumeration.length - 1] = translation;
                //state = States.defaultState;
              } else {
                errorsWithNumeration.push(translation);

                if(stateConfig[error]) {
                  state = stateConfig[error];
                }
              }
            }
          });
      }


      let item: any = errorsWithNumeration;

      if(join) {
        item = item.join(' &rarr; ');
      } else {
        const field = err.slice(0, err.length - 1)
          .filter((e:any)=>!!e)
          .join('.');

        const translatedField = this.t(field);

        const error = err[err.length - 1];

        const getMessage = (error)=>{
          if(typeof error === 'string'){
            return this.t(error, [ translatedField ])
          }
          return error?.translated ? error?.message : this.t(error, [ this.t(error?.message) ])
        }
        
        item = {
          type: field,
          message: getMessage(error)
        } as Error;
      }


      if(numerationState === NumerationState.hasNumeration) {
        resultNumeration.unshift(item);
      } else {
        result.push(item);
      }
    }

    if(message) {
      result.unshift(this.t(message));
    }

    return result.concat(resultNumeration);
  }

  private defaultParseMethod(data: any, numerate: boolean = false): string[][] {
    // errors can be in OBJECT format errors: {"paymentMethodId":[{"message":"Your card's security code is incorrect.","translated":true}]}
    // errors can be in STRING format errors: {custom2Param: ["VALIDATION_REQUIRED"]}

    let result: any[] = [];

    if (typeof data === 'string') {
      console.error('string is not allowed');
      return result;
    }


    if(data) {
      let keys = Object.keys(data);

      for(let i of keys) {

        let error = data[i];

        let stack: StackNode[] = [
          {
            names: [ i ],
            value: error
          }
        ];

        do {
          let node: StackNode = stack.pop() as StackNode;
          let value: any = node.value;

          if (typeof value == 'string')
            continue;

          if(!Array.isArray(value)) {
            let objKeys = Object.keys(value);

            for(let objKey of objKeys) {
              stack.push({
                names: [ ...node.names, objKey ],
                value: value[objKey]
              });
            }
            continue;
          }

          for(let j in value) {
            const curValue = _.get(value, j)

            const numeration: number | string = numerate ? (Number(j) + 1) : '';

            if(typeof curValue === 'string'){
              result.push([ ...node.names, numeration, curValue ]);
            }
            else if(Array.isArray(curValue)){
              result.push([ ...node.names, numeration, ...(curValue as any[]) ]);
            }
            else if(curValue?.message){
              result.push([ ...node.names, numeration, {...(curValue as any[])} ]);
            }
            else if(typeof value === 'object'){
              let objKeys = Object.keys(curValue || {});
              for(let objKey of objKeys) {
                stack.push({
                  names: [ ...node.names, numeration as any, objKey ],
                  value: curValue[objKey]
                });
              }
            }
          }

        } while(stack.length > 0);
      }
    }

    return result;
  }
}

/*
* Parse server validation errors
* */
function validation(errors: any, status: number) {

  if (!errors) return false;
  if (Array.isArray(errors) && errors.length) {
    return 'error:' + status;
  }

  const err = Object.keys(errors);

  if (!err.length) return false;

  try {
    let result: any = {};
    for (let i in err) {
      let name = err[i];
      let error = name + '_' + errors[name][0];
      result[name + ':error'] = VueJS.prototype.t(error);
    }
    return result;
  } catch (e) {
    return false;
  }
}

/*
* Decorator for Form Submit method
* Sets decorator
* */
function Send(nestedErrors: boolean = false) {
  return function(_target: any, key: any, descriptor: any) {
    const original = descriptor.value;

    descriptor.value = function setter() {
      const args = [];
      for (let _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
      }

      this.resetErrors();

      this.state = {
        ...this.state,
        loading: true,
        error: false
      };

      const value = original.apply(this, args);
      if (value) {
        return value
          .then((r: any) => {
            this.state = {
              loading: false,
              error: false
            };
            return Promise.resolve(r);
          })
          .catch((e: any) => {
            // error 403 caught in src/services/errors.ts   and Network Error
            if(e && (e?.response?.status === 403 || e?.response?.status === 400 || e.message==='Network Error')) {
              return this.state = {
                ...this.state,
                loading: false
              };
            }
            
            // plan limits errors, some of them shown in toasts
            if(e?.response?.status === 409 && showPlanLimitsErrorInToast(e)) {
              return this.state = {
                ...this.state,
                loading: false
              };
            }

            if(!e || !e.response) {
              this.state = {
                ...this.state,
                loading: false,
                error: true
              };

              return Promise.reject(e);
            }

            let errors: any;
            if(!nestedErrors) {
              errors = validation(
                e.response.data.errors,
                e.response.data.code || e.response.status
              );
            } else {
              let translatedErrors: Error[] = this.translateErrors(this.parseErrors(e.response.data.errors), '', { join: false });
              errors = {};
              translatedErrors.forEach((error) => {
                errors[`${error.type}:error`] = error.message;
              });
            }

            if (_.isEmpty(errors)) {
              errors = { server_error: 'error:' + e.response.status };
            }

            this.state = {
              ...this.state,
              ...errors,
              loading: false,
              error: true
            };

            if (typeof errors === 'string') {
              this.$emit(errors, e.response);
            } else {
              let keys = Object.keys(errors);
              keys.forEach((error: any) => {
                if (error === 'server_error'){
                  this.$emit('error', e.response, errors);
                  //@ts-ignore
                  this.$emit(errors[error], e.response);
                }
                else {
                  this.$emit(error, e.response);
                  this.$emit('error', e.response, errors);
                }
              });
            }
            return Promise.reject(e);
          });
      } else {
        return Promise.resolve(value);
      }
    };
  };
}

export { Send };
